import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Edit, Trash2, Bot, Eye, EyeOff, Palette } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { aiAssistantService } from "@/services/aiAssistantService";
import { Tables } from "@/types/database.types";
import { AIAssistantForm } from "@/components/super-admin/AIAssistantForm";
import { Link } from "react-router-dom";

type AIAssistant = Tables<"ai_assistants">;

const AIAssistantsPage = () => {
  const [selectedAssistant, setSelectedAssistant] =
    useState<AIAssistant | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get all AI assistants (including inactive ones for admin view)
  const { data: assistants, isLoading } = useQuery({
    queryKey: ["super-admin", "ai-assistants"],
    queryFn: async () => {
      const { data, error } = await aiAssistantService.getAllSystemAssistants();
      if (error) throw error;
      return data || [];
    },
  });

  // Create assistant mutation
  const createMutation = useMutation({
    mutationFn: aiAssistantService.createSystemAssistant,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "ai-assistants"],
      });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "AI Assistant created successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update assistant mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      aiAssistantService.updateSystemAssistant(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "ai-assistants"],
      });
      setIsEditDialogOpen(false);
      setSelectedAssistant(null);
      toast({
        title: "Success",
        description: "AI Assistant updated successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle active status
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      aiAssistantService.updateSystemAssistant(id, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["super-admin", "ai-assistants"],
      });
      toast({
        title: "Success",
        description: "AI Assistant status updated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleCreate = (assistantData: any) => {
    createMutation.mutate(assistantData);
  };

  const handleUpdate = (assistantData: any) => {
    if (!selectedAssistant) return;
    updateMutation.mutate({
      id: selectedAssistant.id,
      updates: assistantData,
    });
  };

  const handleToggleActive = (assistant: AIAssistant) => {
    toggleActiveMutation.mutate({
      id: assistant.id,
      isActive: !assistant.is_active,
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Marketing":
        return "bg-blue-100 text-blue-800";
      case "Sales":
        return "bg-green-100 text-green-800";
      case "Daily work":
        return "bg-amber-100 text-amber-800";
      case "Customer Engagement":
        return "bg-cyan-100 text-cyan-800";
      case "Operations":
        return "bg-purple-100 text-purple-800";
      case "Legal":
        return "bg-rose-100 text-rose-800";
      case "Finance":
        return "bg-indigo-100 text-indigo-800";
      case "HR":
        return "bg-emerald-100 text-emerald-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <SuperAdminLayout
          title="AI Assistants"
          description="Manage system AI assistants"
        >
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
        </SuperAdminLayout>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SuperAdminLayout
        title="AI Assistants"
        description="Manage system AI assistants available to all organizations"
        actions={
          <div className="flex gap-3">
            <Button variant="outline" asChild>
              <Link to="/super-admin/ai-assistants/formatting">
                <Palette className="h-4 w-4 mr-2" />
                Formatting Guide
              </Link>
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create AI Tool
            </Button>
          </div>
        }
      >
        {/* Assistants Table */}
        <Card>
          <CardHeader>
            <CardTitle>AI Assistants</CardTitle>
            <CardDescription>
              Manage system-level AI assistants that organizations can use
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Assistant</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>CTA Label</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assistants?.map((assistant) => (
                  <TableRow
                    key={assistant.id}
                    className={!assistant.is_active ? "opacity-60" : ""}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {assistant.icon_url ? (
                          <img
                            src={assistant.icon_url}
                            alt={assistant.name}
                            className={`w-8 h-8 rounded-lg object-cover ${!assistant.is_active ? "grayscale" : ""}`}
                          />
                        ) : (
                          <div
                            className={`w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center ${!assistant.is_active ? "bg-gray-100" : ""}`}
                          >
                            <Bot
                              className={`h-4 w-4 ${assistant.is_active ? "text-primary" : "text-gray-400"}`}
                            />
                          </div>
                        )}
                        <div>
                          <p
                            className="font-medium cursor-pointer hover:text-primary transition-colors"
                            onClick={() => {
                              setSelectedAssistant(assistant);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            {assistant.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {assistant.name}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCategoryColor(assistant.category)}>
                        {assistant.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={assistant.is_active ? "default" : "secondary"}
                        className={
                          assistant.is_active
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-gray-100 text-gray-600"
                        }
                      >
                        {assistant.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {assistant.cta_label}
                      </code>
                    </TableCell>
                    <TableCell>
                      {new Date(assistant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedAssistant(assistant);
                            setIsViewDialogOpen(true);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(assistant)}
                        >
                          {assistant.is_active ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedAssistant(assistant);
                            setIsEditDialogOpen(true);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create Side Panel */}
        <Sheet open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <SheetContent className="w-[70%] max-w-none p-0 overflow-hidden">
            <div className="flex flex-col h-full">
              <SheetHeader className="p-6 pb-4 border-b">
                <SheetTitle>Create AI Assistant</SheetTitle>
                <SheetDescription>
                  Create a new system-level AI assistant that organizations can
                  use
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-6">
                <AIAssistantForm
                  onSubmit={handleCreate}
                  onCancel={() => setIsCreateDialogOpen(false)}
                  isLoading={createMutation.isPending}
                />
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Edit Side Panel */}
        <Sheet open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <SheetContent className="w-[70%] max-w-none p-0 overflow-hidden">
            <div className="flex flex-col h-full">
              <SheetHeader className="p-6 pb-4 border-b">
                <SheetTitle>Edit AI Assistant</SheetTitle>
                <SheetDescription>
                  Update the AI assistant configuration
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto p-6">
                {selectedAssistant && (
                  <AIAssistantForm
                    initialData={selectedAssistant}
                    onSubmit={handleUpdate}
                    onCancel={() => setIsEditDialogOpen(false)}
                    isLoading={updateMutation.isPending}
                  />
                )}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>AI Assistant Details</DialogTitle>
              <DialogDescription>
                View AI assistant configuration and details
              </DialogDescription>
            </DialogHeader>
            {selectedAssistant && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-medium">Name</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedAssistant.name}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Title</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedAssistant.title}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedAssistant.category}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">CTA Label</label>
                    <p className="text-sm text-muted-foreground">
                      {selectedAssistant.cta_label}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Description</label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedAssistant.description}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium">Instructions</label>
                  <pre className="text-sm bg-muted p-3 rounded-md mt-1 whitespace-pre-wrap">
                    {selectedAssistant.base_instructions}
                  </pre>
                </div>

                <div>
                  <label className="text-sm font-medium">Form Fields</label>
                  <pre className="text-sm bg-muted p-3 rounded-md mt-1">
                    {JSON.stringify(selectedAssistant.form_fields, null, 2)}
                  </pre>
                </div>

                <div>
                  <label className="text-sm font-medium">Output Schema</label>
                  <pre className="text-sm bg-muted p-3 rounded-md mt-1">
                    {JSON.stringify(selectedAssistant.zod_schema, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsViewDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default AIAssistantsPage;
